'use client';

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faBullseye, faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { useGetMyChainedStrategiesQuery } from 'src/api/redux';
import { CircularProgress } from 'src/components/PulseUI';
import { useLogging } from 'src/context/logging';
import { LOCATION } from 'src/hooks/filters/constants';
import useStrategies from 'src/hooks/strategy/useStrategies';
import {
  useClickOutside,
  useDropdownPosition,
  useResizeClose,
} from 'src/hooks/common';
import { StrategyItem } from './components';

// Color classes
const DOT_CLASSES = [
  'bg-orange-500',
  'bg-teal-500',
  'bg-purple-500',
  'bg-sky-500',
  'bg-pink-500',
  'bg-green-500',
  'bg-red-500',
];

const StrategyDropdown = () => {
  const log = useLogging();
  const { selectedChainIds, setSelectedChainIds: onSelectionChange } = useStrategies(LOCATION.STRATEGY_CENTER);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const {
    currentData: chainedStrategies,
    isLoading,
    isFetching,
  } = useGetMyChainedStrategiesQuery();

  const { dropdownStyle, calculatePosition } = useDropdownPosition(
    buttonRef,
    400
  );

  const closeDropdown = useCallback(() => setIsOpen(false), []);
  useClickOutside([buttonRef, dropdownRef], closeDropdown, isOpen);
  useResizeClose(closeDropdown, isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const strategies = useMemo(() => {
    if (!chainedStrategies?.results) return [];
    return [...chainedStrategies.results].sort((a, b) =>
      (a?.label || '').localeCompare(b?.label || '')
    );
  }, [chainedStrategies?.results]);

  // Map each chain ID to a dot color class based on sorted position
  const dotClassMap = useMemo(() => {
    const map = new Map<number, string>();
    const sortedIds = strategies
      .map((s: any) => s.id)
      .sort((a: number, b: number) => a - b);
    sortedIds.forEach((id: number, index: number) => {
      map.set(id, DOT_CLASSES[index % DOT_CLASSES.length]);
    });
    return map;
  }, [strategies]);

  const allIds = useMemo(() => strategies.map((s: any) => s.id), [strategies]);

  // Initialize selection to all when strategies first load (once),
  // and clean up stale chain IDs that no longer exist in available strategies
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (strategies.length === 0) return;

    const validIds = selectedChainIds.filter((id) => allIds.includes(id));

    if (!hasInitialized.current && selectedChainIds.length === 0) {
      // First load with no saved selection — select all
      hasInitialized.current = true;
      onSelectionChange(allIds);
    } else if (validIds.length !== selectedChainIds.length) {
      // Remove stale IDs that reference deleted/removed strategies
      hasInitialized.current = true;
      onSelectionChange(validIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategies]);
  const hasSelection = selectedChainIds.length > 0;
  const allSelected = allIds.length > 0 && selectedChainIds.length === allIds.length;
  const singleSelected =
    selectedChainIds.length === 1
      ? strategies.find((s: any) => s.id === selectedChainIds[0])
      : null;

  const handleToggle = (chainId: number) => {
    const isSelected = selectedChainIds.includes(chainId);
    const next = isSelected
      ? selectedChainIds.filter((id) => id !== chainId)
      : [...selectedChainIds, chainId];
    onSelectionChange(next);
    log.event('Strategy chain toggled', { chainId, selected: !isSelected });
  };

  const handleSelectAll = () => {
    onSelectionChange(allIds);
    log.event('Strategy chains select all');
  };

  const handleClear = () => {
    onSelectionChange([]);
    log.event('Strategy chains cleared');
  };

  const handleClick = () => {
    if (!isOpen) {
      calculatePosition();
    }
    setIsOpen(!isOpen);
  };

  return (
    <>
      <div data-testid="strategy-dropdown">
        <button
          ref={buttonRef}
          data-testid="dropdown-button"
          type="button"
          onClick={handleClick}
          className={`p-2 @[1000px]/header:px-4 @[1000px]/header:py-2 rounded-full border inline-flex items-center gap-2 cursor-pointer transition-colors ${
            hasSelection
              ? 'bg-neutral-100 border-neutral-300 hover:bg-neutral-200'
              : 'bg-white border-neutral-200 hover:bg-neutral-50'
          }`}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label="Select strategies"
        >
          <Icon icon={faBullseye} className="text-tertiary" />
          {singleSelected ? (
            <>
              <span
                className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                  dotClassMap.get(singleSelected.id) || DOT_CLASSES[0]
                }`}
                aria-hidden="true"
              />
              <span className="hidden @[1000px]/header:inline text-primary text-sm font-semibold">
                {singleSelected.label}
              </span>
            </>
          ) : hasSelection ? (
            <>
              <div className="flex items-center -space-x-1">
                {selectedChainIds.map((id) => (
                  <span
                    key={id}
                    className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ring-1 ring-white ${
                      dotClassMap.get(id) || DOT_CLASSES[0]
                    }`}
                    aria-hidden="true"
                  />
                ))}
              </div>
              <span className="hidden @[1000px]/header:inline text-primary text-sm font-semibold">
                {allSelected ? 'All Strategies' : `${selectedChainIds.length} selected`}
              </span>
            </>
          ) : (
            <span className="hidden @[1000px]/header:inline text-primary text-sm font-semibold">None</span>
          )}
          <Icon icon={faChevronDown} className={`hidden @[1000px]/header:inline text-tertiary transition-all duration-300 ${isOpen ? '-rotate-90' : 'rotate-0'}`} />
        </button>
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            data-testid="dropdown-menu"
            className="w-96 bg-white rounded-2xl border border-neutral-200 shadow-lg"
            style={dropdownStyle}
            role="listbox"
            aria-multiselectable="true"
          >
            <div className="px-4 pt-4 pb-2">
              <div className="text-xs font-semibold text-tertiary">
                Strategies
              </div>
            </div>

            <div className="max-h-[50vh] overflow-y-auto py-1">
              {isLoading || isFetching ? (
                <div className="flex items-center justify-center h-32">
                  <CircularProgress size="sm" color="neutral" />
                </div>
              ) : (
                strategies.map((strategy: any) => (
                  <StrategyItem
                    key={strategy.id}
                    strategy={strategy}
                    isChecked={selectedChainIds.includes(strategy.id)}
                    dotClass={dotClassMap.get(strategy.id) || DOT_CLASSES[0]}
                    onToggle={handleToggle}
                  />
                ))
              )}
            </div>

            <div className="border-t border-neutral-200 px-4 py-3 flex flex-col gap-2">
              <button
                type="button"
                data-testid="select-all-button"
                onClick={handleSelectAll}
                className="text-sm font-semibold text-primary text-left hover:underline"
                aria-label="Select all strategies"
              >
                Select all
              </button>
              <button
                type="button"
                data-testid="clear-button"
                onClick={handleClear}
                className="text-sm text-tertiary text-left hover:underline"
                aria-label="Clear strategy selection"
              >
                Clear
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default StrategyDropdown;
